/**
 * @matchday/client — track.ts
 *
 * The event-tracking half of the client SDK (KICKOFF §2.4: client SDK `track()`
 * typed from packages/contracts → ingest → queue → ClickHouse).
 *
 * - `track(eventType, payload)` is typed from the contracts event taxonomy;
 *   EVENT_PAYLOADS validates every payload at the boundary (zod), so a bad
 *   payload throws at the call site, never lands in the pipeline.
 * - Batching: flush at BATCH_MAX (20) events or FLUSH_INTERVAL_MS (5s),
 *   whichever first.
 * - Offline queue: the in-memory queue is authoritative; an injectable
 *   persistence adapter mirrors it (default in-memory; the interface is
 *   shaped for an IndexedDB implementation later — async, whole-queue
 *   snapshots, restore on boot).
 * - sendBeacon-style flush: `drainForBeacon()` synchronously drains the queue
 *   and hands the caller the events for `navigator.sendBeacon` on pagehide.
 * - Transport is an interface; HttpTransport (fetch → POST /ingest) for the
 *   real app, InMemoryTransport for tests and the PROOFER harness.
 */
import {
  EVENT_PAYLOADS,
  TrackedEvent,
  type EventPayload,
  type EventType,
  type SessionId,
} from "@matchday/contracts";
import type { z } from "zod";

// ── Transport ────────────────────────────────────────────────────────────────

export interface TrackTransport {
  send(events: TrackedEvent[]): Promise<void>;
}

export interface HttpTransportOptions {
  /** Base URL of the ingest service, e.g. "http://localhost:8789". */
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

/** Real transport: POST TrackedEvent[] to `${baseUrl}/ingest`. */
export function createHttpTransport(options: HttpTransportOptions): TrackTransport {
  const fetchImpl = options.fetchImpl ?? fetch;
  const url = `${options.baseUrl.replace(/\/$/, "")}/ingest`;
  return {
    async send(events: TrackedEvent[]): Promise<void> {
      const res = await fetchImpl(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(events),
        keepalive: true,
      });
      if (!res.ok) {
        throw new Error(`ingest rejected batch: HTTP ${res.status}`);
      }
    },
  };
}

/** Test/harness transport: records batches; can simulate offline periods. */
export class InMemoryTransport implements TrackTransport {
  readonly batches: TrackedEvent[][] = [];
  /** Number of upcoming send() calls that should fail (offline simulation). */
  failNextSends = 0;

  get events(): TrackedEvent[] {
    return this.batches.flat();
  }

  async send(events: TrackedEvent[]): Promise<void> {
    if (this.failNextSends > 0) {
      this.failNextSends -= 1;
      throw new Error("InMemoryTransport: simulated offline");
    }
    this.batches.push([...events]);
  }
}

// ── Offline-queue persistence (IndexedDB-ready interface) ────────────────────

export interface QueuePersistence {
  /** Restore any events persisted by a previous session (crash / reload). */
  restore(): Promise<TrackedEvent[]>;
  /** Persist the current queue snapshot. Called after every queue mutation. */
  persist(queue: readonly TrackedEvent[]): Promise<void>;
}

export class InMemoryQueuePersistence implements QueuePersistence {
  private snapshot: TrackedEvent[] = [];

  async restore(): Promise<TrackedEvent[]> {
    return [...this.snapshot];
  }

  async persist(queue: readonly TrackedEvent[]): Promise<void> {
    this.snapshot = [...queue];
  }
}

// ── Tracker ──────────────────────────────────────────────────────────────────

export const BATCH_MAX = 20;
export const FLUSH_INTERVAL_MS = 5_000;

export interface TrackerOptions {
  transport: TrackTransport;
  /** Session envelope fields; null before a session exists (pre-auth landing). */
  sessionId: SessionId | null;
  deviceHash: string | null;
  /** Injectable clock — the harness drives time explicitly. */
  now?: () => Date;
  maxBatch?: number;
  flushIntervalMs?: number;
  persistence?: QueuePersistence;
  /** Non-throwing error sink (persistence/transport failures). */
  onError?: (error: unknown) => void;
}

export interface Tracker {
  /** Validate (zod, EVENT_PAYLOADS) + enqueue. Throws on an invalid payload. */
  track<T extends EventType>(eventType: T, payload: EventPayload<T>): void;
  /** Force a flush now. Resolves when the attempt (success or requeue) is done. */
  flush(): Promise<void>;
  /**
   * sendBeacon-style hook: synchronously drain everything queued and return it.
   * Wire on pagehide:
   *   const events = tracker.drainForBeacon();
   *   if (events.length && !navigator.sendBeacon(url, JSON.stringify(events))) {
   *     tracker.requeue(events); // beacon refused — put them back
   *   }
   */
  drainForBeacon(): TrackedEvent[];
  /** Put events back on the queue (front) — beacon refusal / manual retry. */
  requeue(events: TrackedEvent[]): void;
  /** Restore persisted events from a previous session into the queue. */
  restore(): Promise<void>;
  /** Update envelope identity after auth (session starts null pre-OTP). */
  setSession(sessionId: SessionId | null, deviceHash?: string | null): void;
  pending(): number;
  /** Clears the flush timer and attempts a final flush. Tests MUST call this. */
  dispose(): Promise<void>;
}

export function createTracker(options: TrackerOptions): Tracker {
  const now = options.now ?? (() => new Date());
  const maxBatch = options.maxBatch ?? BATCH_MAX;
  const flushIntervalMs = options.flushIntervalMs ?? FLUSH_INTERVAL_MS;
  const persistence = options.persistence ?? new InMemoryQueuePersistence();
  const onError = options.onError ?? (() => undefined);

  let sessionId: SessionId | null = options.sessionId;
  let deviceHash: string | null = options.deviceHash;

  const queue: TrackedEvent[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  // Serialises flushes so two callers never race the same events.
  let flushChain: Promise<void> = Promise.resolve();

  function persistSoon(): void {
    void persistence.persist(queue).catch(onError);
  }

  function clearTimer(): void {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  }

  function scheduleTimer(): void {
    if (flushTimer === null && queue.length > 0) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        void flush();
      }, flushIntervalMs);
    }
  }

  async function doFlush(): Promise<void> {
    clearTimer();
    while (queue.length > 0) {
      const batch = queue.splice(0, maxBatch);
      try {
        await options.transport.send(batch);
        persistSoon();
      } catch (error) {
        // Offline / rejected: put the batch back at the front, keep order,
        // stop trying — the next track()/flush()/timer retries.
        queue.unshift(...batch);
        persistSoon();
        onError(error);
        scheduleTimer();
        return;
      }
    }
  }

  function flush(): Promise<void> {
    flushChain = flushChain.then(doFlush);
    return flushChain;
  }

  function track<T extends EventType>(eventType: T, payload: EventPayload<T>): void {
    // Runtime boundary: the payload schema from contracts is the law.
    const schema: z.ZodTypeAny = EVENT_PAYLOADS[eventType];
    const parsed = schema.parse(payload) as Record<string, unknown>;
    const event = TrackedEvent.parse({
      event_type: eventType,
      event_time: now().toISOString(),
      session_id: sessionId,
      device_hash: deviceHash,
      payload: parsed,
    });
    queue.push(event);
    persistSoon();
    if (queue.length >= maxBatch) {
      void flush();
    } else {
      scheduleTimer();
    }
  }

  return {
    track,
    flush,
    drainForBeacon(): TrackedEvent[] {
      clearTimer();
      const drained = queue.splice(0, queue.length);
      persistSoon();
      return drained;
    },
    requeue(events: TrackedEvent[]): void {
      queue.unshift(...events);
      persistSoon();
      scheduleTimer();
    },
    async restore(): Promise<void> {
      const restored = await persistence.restore();
      if (restored.length > 0) {
        queue.unshift(...restored.map((e) => TrackedEvent.parse(e)));
        scheduleTimer();
      }
    },
    setSession(nextSessionId: SessionId | null, nextDeviceHash?: string | null): void {
      sessionId = nextSessionId;
      if (nextDeviceHash !== undefined) deviceHash = nextDeviceHash;
    },
    pending(): number {
      return queue.length;
    },
    async dispose(): Promise<void> {
      clearTimer();
      await flush();
      clearTimer(); // a failed final flush re-schedules; leave nothing running
    },
  };
}
